import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Download, FileText, ChevronRight } from 'lucide-react';
import { MoldableIcon, BiocompatibleIcon, MechanicalIcon, AntibioticCarrierIcon, FastHealingIcon, NonAnimalIcon, BioactiveIcon, NonToxicIcon, StorageIcon } from '../components/FlexiOssCinematic/PropertyIcons';
import SubpageLayout from '../components/FlexiOssCinematic/SubpageLayout';
import ReadAloud from '../components/ReadAloud';
import SocialShare from '../components/FlexiOssCinematic/SocialShare';
import StructuredData from '../components/StructuredData';
import { vetSchema } from '../data/productSchema';
import { HorizontalBorderStack, EditorialColumnSplit } from '../components/editorial/EditorialLayouts';
import { ProductPropertiesEditorial, ProductPublications } from '../components/product/ProductSections';
import ProductShareBar from '../components/product/ProductShareBar';
import { SITE_URL } from '../lib/siteConfig';

const VET_SHARE_SUMMARY = [
  'FlexiOss® Vet — 3rd-generation synthetic bone graft (hydroxyapatite + curdlan polymer) for companion-animal orthopedic & dental surgery.',
  '',
  'KEY FACTS',
  '• 100% synthetic and animal-free — no zoonotic, prion or virus risk; CE certified (EC 1434-MDD-269/2021).',
  '• Moldable after soaking; absorbs blood, plasma and drug solutions; acts as a local antibiotic carrier; haemostatic.',
  '• Bone healing up to ~50% faster than the standard 56-day baseline.',
  '• Sizes: 2, 5, 10, 30 and 50 mm (Ø13 mm cylinders); Start / Orthopaedic / Dentistry sets available.',
  '',
  'PROVEN IN',
  '• Dogs, cats and horses — extraction-site filling, oronasal fistulae, periodontal & EOTRH disease, angular-deformity correction and fracture repair.',
].join('\n');

const ACCENT = '#4CAF50';

const API = process.env.REACT_APP_BACKEND_URL;
const downloadFile = (url, filename) => {
  const href = /^https?:\/\//i.test(url) && (!API || !url.startsWith(API))
    ? `${API}/api/download?url=${encodeURIComponent(url)}&name=${encodeURIComponent(filename || 'FlexiOss.pdf')}`
    : url;
  const link = document.createElement('a');
  link.href = href;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/* ─── Data ─── */
const properties = [
  { icon: MoldableIcon, title: 'Flexible After Soaking', desc: 'Plastic and bendable — fits individual bone defects by cutting or bending' },
  { icon: BiocompatibleIcon, title: 'Bio-Compatible', desc: 'Compatible with natural bone tissue for seamless integration' },
  { icon: MechanicalIcon, title: 'Mechanical Parameters', desc: 'Similar to those of cancellous bone for structural support' },
  { icon: AntibioticCarrierIcon, title: 'Antibiotic Carrier', desc: 'Can be soaked in antibacterial drugs, minimizing systemic antibiotic use' },
  { icon: FastHealingIcon, title: 'Bone Healing 50% Faster', desc: 'Reduces standard bone healing time (56 days) by half*' },
  { icon: NonAnimalIcon, title: 'Non-Animal Material', desc: 'No risk of zoonotic disease, virus or prion contamination' },
  { icon: BioactiveIcon, title: 'Ionic Reactive / Bioactive', desc: 'Actively supports bone regeneration through enhanced ion uptake' },
  { icon: NonToxicIcon, title: 'Non-Toxic Material', desc: 'Thoroughly tested for safety — no cytotoxicity observed' },
  { icon: StorageIcon, title: 'Easy to Store & Transport', desc: 'Simple storage requirements — no special conditions needed' },
];

const productSizes = [
  { size: '2 mm', pieces: '3', ean: '5904326324107 (3 pcs.)', dimL: 'L = 2 mm ± 1 mm', dimD: 'Ø 13 mm ± 0.5 mm', weight: '0.16 g ± 0.02 g', geometry: 'Roller / Cylinder' },
  { size: '5 mm', pieces: '3', ean: '5904326324138 (3 pcs.)', dimL: 'L = 5 mm ± 1 mm', dimD: 'Ø 13 mm ± 0.5 mm', weight: '0.42 g ± 0.4 g', geometry: 'Roller / Cylinder' },
  { size: '10 mm', pieces: '1', ean: '5904326324022 (1 pc.)', dimL: 'L = 10 mm', dimD: 'Ø 13 mm', weight: '0.89 g ± 0.1 g', geometry: 'Roller / Cylinder' },
  { size: '30 mm', pieces: '1', ean: '5904326324046 (1 pc.)', dimL: 'L = 30 mm', dimD: 'Ø 13 mm', weight: '2.49 g ± 0.2 g', geometry: 'Roller / Cylinder' },
  { size: '50 mm', pieces: '1', ean: '5904326324060 (1 pc.)', dimL: 'L = 50 mm', dimD: 'Ø 13 mm', weight: '4.18 ± 0.35 g', geometry: 'Roller / Cylinder' },
];

const availableSets = [
  { name: 'Start Set', contents: ['FlexiOss® Vet 2 mm × 1 pc.', 'FlexiOss® Vet 5 mm × 1 pc.', 'FlexiOss® Vet 10 mm × 1 pc.'], use: 'Dentistry, Orthopaedics, Traumatology' },
  { name: 'Orthopaedic Set', contents: ['FlexiOss® Vet 10 mm × 1 pc.', 'FlexiOss® Vet 30 mm × 1 pc.'], use: 'Orthopaedics, Traumatology' },
  { name: 'Dentistry Set', contents: ['FlexiOss® Vet 2 mm × 3 pcs.', 'FlexiOss® Vet 5 mm × 3 pcs.', 'FlexiOss® Vet 10 mm × 1 pc.'], use: 'Dentistry, Oral Surgery' },
];

const caseStudies = [
  { category: 'Orthopaedics', title: 'Angular deformity of both tibias. Pes Varus, bilaterally, open-wedge tibial osteotomy in two stages', url: 'https://flexiossvet.pl/en/zastosowanie/angular-deformity-of-both-tibias/' },
  { category: 'Orthopaedics', title: 'Lameness of the right foreleg. Radioulnar mismatch at the elbow joint due to a short radius bone', url: 'https://flexiossvet.pl/en/zastosowanie/lameness-of-the-right-foreleg/' },
  { category: 'Dentistry', title: 'Complicated tooth fracture. Extraction of teeth 104 and 204', url: 'https://flexiossvet.pl/en/zastosowanie/complicated-tooth-fracture-extraction-of-teeth-104-and-204/' },
  { category: 'Dentistry', title: 'Complicated ectopic tooth. Tooth extraction and curettage of an odontogenic cyst', url: 'https://flexiossvet.pl/en/zastosowanie/complicated-ectopic-tooth-tooth-extraction-and-curettage-of-an-odontogenic-cyst/' },
  { category: 'Dentistry', title: 'Osteitis around canine 404, pulp necrosis of tooth 404, huge periapical lesion with partial lysis of the mandibular bone', url: 'https://flexiossvet.pl/en/zastosowanie/osteitis-around-canine-404-pulp-necrosis-of-tooth-404/' },
  { category: 'Dentistry', title: 'Stage IV stomatitis, severely advanced periodontal disease', url: 'https://flexiossvet.pl/en/zastosowanie/stage-iv-stomatitis-severely-advanced-periodontal-disease/' },
  { category: 'Dentistry', title: 'Advanced EOTRH syndrome in the upper and lower incisors. Periodontitis around teeth 309, 310', url: 'https://flexiossvet.pl/en/zastosowanie/advanced-eotrh-syndrome-in-the-upper-and-lower-incisors/' },
  { category: 'Dentistry', title: 'EOTRH syndrome. Periapical infection of tooth 309', url: 'https://flexiossvet.pl/en/zastosowanie/eotrh-syndrome-periapical-infection-of-tooth-309/' },
];

const publications = [
  { title: 'Determining Hydroxyapatite Filling Volume for the Treatment of Post-Extraction Alveoli Based on Measurements of Alveolar Volume in Relation to the Body Weight of Dogs', authors: 'Misztal-Kunecka A., Przadka P., Dzimira St.', year: '2025', pdfUrl: 'https://flexiossvet.pl/wp-content/uploads/2025/03/manuscript.v10.pdf' },
  { title: 'The Use of Hydroxyapatite Polymer with Curdlan in the Treatment of Bone Defects Associated with Ectopic Tooth Extraction in Dogs — A Case Series', authors: 'Misztal-Kunecka A., Przadka P., Dzimira St.', journal: 'Life 14, 879', year: '2024', pdfUrl: 'https://flexiossvet.pl/wp-content/uploads/2024/07/life-14-00879.pdf' },
  { title: 'Evaluation of post-extraction alveolar regeneration time in advanced periodontal disease employing novel hydroxyapatite-polymeric (FlexiOss®Vet) in dogs', authors: 'Misztal-Kunecka A., Dzimira St., Lojszczyk A.', year: '2024', pdfUrl: 'https://flexiossvet.pl/wp-content/uploads/2024/02/A.Misztal-Kunecka-Evaluation-of-post-extraction-alveolar-regeneration-time-in-advanced-periodontal-disease.pdf' },
  { title: 'Behavior of new hydroxyapatite / glucan composite in human serum', authors: 'Borkowski L., Lubek T., Jojczuk M., Nogalski A., Belcarz A., Palka K., Hajnos M., Ginalska G.', journal: 'J. Biomed. Mater. Res. Part B: Applied Biomaterials, 2018', year: '2018', pdfUrl: 'https://flexiossvet.pl/wp-content/uploads/2023/09/Journal_of_Biomedical_Materials_Research_Part_B__Applied_Biomaterials_2018.pdf' },
  { title: 'Unexpected reaction of new HAp / glucan composite to environmental acidification: Defect or advantage?', authors: 'Borkowski L., Kiernicka M., Belcarz A., Palka K., Hajnos M., Ginalska G.', year: '2016', externalUrl: 'https://onlinelibrary.wiley.com/doi/abs/10.1002/jbm.b.33646' },
  { title: 'The comparison study of bioactivity between composites containing synthetic non-substituted and carbonate-substituted hydroxyapatite', authors: 'Borkowski L., Sroka-Bartnicka A., Draczkowski P., Ptak A., Zieba E., Slosarczyk A., Ginalska G.', journal: 'Mater. Sci. Eng. C Mater. Biol. Appl.', year: '2016', pdfUrl: 'https://flexiossvet.pl/wp-content/uploads/2023/09/The-comparison-study-of-bioactivity-between-composites-containing.pdf' },
  { title: 'Biphasic mode of antibacterial action of aminoglycoside antibiotics-loaded elastic hydroxyapatite-glucan composite', authors: 'Belcarz A., Zima A., Ginalska G.', journal: 'Int. J. Pharm. 2013', year: '2013', pdfUrl: 'https://flexiossvet.pl/wp-content/uploads/2023/09/EJP-2013.pdf' },
  { title: 'Application of β-1,3-glucan in production of ceramics-based elastic composite for bone repair', authors: 'Belcarz A., Ginalska G., Pycka T., Zima A., Slosarczyk A., Polkowska I., Paszkiewicz Z., Piekarczyk W.', journal: 'Centr. Eur. J. Biol. 2013', year: '2013', pdfUrl: 'https://flexiossvet.pl/wp-content/uploads/2023/09/Application-of-B-13-glucan-in-production-of.pdf' },
];

const clinicalApps = [
  'Teeth extraction site filling and bone preservation',
  'Oronasal fistulae treatment and closure',
  'Angular deformity correction (tibial osteotomy)',
  'Periodontal disease and bone regeneration',
  'EOTRH syndrome (Equine Odontoclastic Tooth Resorption)',
  'Mandibular and maxillary fracture stabilization',
  'Post-trauma bone defect reconstruction',
  'Cyst removal and cavity filling',
  'Long bone fracture augmentation',
  'Joint fusion procedures (arthrodesis)',
];

/* ─── Page Component ─── */
const FlexiOssVetPage = () => {
  const [caseFilter, setCaseFilter] = useState('All');
  const specRef = useRef(null);
  const specInView = useInView(specRef, { once: true, margin: '-60px' });

  const filteredCases = caseFilter === 'All'
    ? caseStudies
    : caseStudies.filter((c) => c.category === caseFilter);

  return (
    <SubpageLayout productName="Vet">
      <StructuredData id="vet-page" schema={vetSchema} />
      {/* ─── Hero ─── */}
      <section className="relative bg-gradient-to-b from-slate-900 to-slate-950 pt-16 pb-24 overflow-hidden" data-testid="vet-hero">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#4CAF50]/10 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <p className="uppercase text-xs font-bold tracking-[0.2em] text-[#4CAF50] mb-3">3rd Generation Veterinary Biomaterial</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-white leading-[1.08] mb-4" >
                FlexiOss<sup className="text-xl lg:text-2xl align-super">®</sup> <span className="text-[#4CAF50]">Vet</span>
              </h1>
              <p className="text-lg text-white/60 font-medium mb-6 leading-relaxed">
                Modern implantable biomaterial — biphasic (hydroxyapatite-polymeric), bioactive, supporting bone tissue regeneration in companion animals.
              </p>
              <p className="text-sm text-white/45 mb-8 leading-relaxed max-w-lg">
                FlexiOss® Vet absorbs liquids (blood, plasma, drug solutions). It serves as a carrier of active substances, particularly antibacterial drugs, minimizing systemic antibiotic use. Its haemostatic properties reduce bleeding from the operation site.
              </p>
              <div className="mb-6"><ReadAloud accent={ACCENT} /></div>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => downloadFile(`${API}/api/document/vet-ifu`, 'FlexiOss_Vet_IFU.pdf')} data-testid="vet-download-ifu" className="bg-[#4CAF50] hover:bg-[#388E3C] text-white font-bold px-6 py-3 rounded-lg text-sm inline-flex items-center gap-2 shadow-2xl shadow-[#4CAF50]/50 hover:-translate-y-0.5 transition-all">
                  <Download className="w-4 h-4" /> Download IFU
                </button>
                <button onClick={() => downloadFile(`${API}/api/document/vet-catalogue`, 'FlexiOss_Vet_Catalogue.pdf')} data-testid="vet-download-brochure" className="border-2 border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50]/10 font-bold px-6 py-3 rounded-lg text-sm inline-flex items-center gap-2 transition-all">
                  <FileText className="w-4 h-4" /> Product Catalogue
                </button>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative flex items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-[#00A676]/15 blur-[80px] scale-110" />
              <img src="/flexioss-vet.png" alt="FlexiOss Vet — 3rd generation veterinary bone regenerative biomaterial for companion animal orthopedic and dental surgery" className="w-full max-w-sm h-auto object-contain relative z-10 drop-shadow-2xl" />
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-slate-900 to-transparent" />
      </section>

      {/* ─── Videos ─── */}
      <section className="py-20 bg-slate-900" data-testid="vet-videos">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="uppercase text-xs font-bold tracking-[0.2em] text-[#4CAF50] mb-3">Video Demonstrations</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white" >See FlexiOss® Vet in Action</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { id: 'o2g2neX3ZXs', title: 'Product Animation', desc: '3D animation showcasing the biomaterial structure, porosity, and integration process.' },
              { id: '2a9EyImuZks', title: 'Veterinary Dentistry', desc: 'Clinical demonstration of tooth extraction, bone filling, and oronasal fistulae treatment.' },
              { id: 'ijp3BOXX4h0', title: 'FlexiOss® Vet Clinical', desc: 'Clinical overview and real-world veterinary applications of FlexiOss® Vet biomaterial.' },
            ].map((vid, i) => (
              <div key={i} className="group border border-slate-700 rounded-2xl overflow-hidden hover:border-[#4CAF50] hover:shadow-lg transition-all duration-300" data-testid={`vet-video-${i}`}>
                <div className="relative aspect-video bg-[#001a33]">
                  <iframe width="100%" height="100%" src={`https://www.youtube.com/embed/${vid.id}`} title={vid.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" loading="lazy" />
                </div>
                <div className="p-5">
                  <h4 className="text-base font-bold text-white mb-1" >{vid.title}</h4>
                  <p className="text-sm text-slate-400 leading-relaxed">{vid.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Properties (editorial) ─── */}
      <ProductPropertiesEditorial
        properties={properties}
        accent={ACCENT}
        testidPrefix="vet"
        bg="bg-slate-950"
        heading={<>Why Choose <span className="text-[#4CAF50]">FlexiOss® Vet</span></>}
        footnote="*In relation to the standard bone healing time, which is 56 days."
      />

      {/* ─── Innovative Material ─── */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="uppercase text-xs font-bold tracking-[0.2em] text-[#4CAF50] mb-3">Innovative Material</p>
              <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-white mb-4" >TriCurean Composition</h2>
              <p className="text-slate-400 leading-relaxed mb-6">
                The combination of the two main compounds was obtained by polymer gelling into the structure of an irreversible, collagen-like triple helix — a conformation which does not evoke local inflammation and enables the entrapment of HAP granulate.
              </p>
              <p className="text-slate-400 leading-relaxed mb-8">
                Once implanted, FlexiOss® Vet integrates favourably into the patient's bone with the effect of natural bone tissue formation. It eliminates the need for reoperation for implant removal, sparing the animal additional stress and pain.
              </p>
              <EditorialColumnSplit
                accent={ACCENT}
                testid="vet-composition"
                items={[
                  { term: 'Hydroxyapatite (HAP)', role: 'Ceramic Phase', def: <HorizontalBorderStack numbered={false} dense accent={ACCENT} testid="vet-hap" items={['Low-sintered, highly porous ceramic', 'Fully synthetic with high chemical stability', 'Supports regeneration through enhanced ion uptake']} /> },
                  { term: 'Curdlan Polymer', role: 'Polymeric Phase', def: <HorizontalBorderStack numbered={false} dense accent={ACCENT} testid="vet-curdlan" items={['Triple-helix structure (collagen-like)', 'Does not stimulate pro-inflammatory processes', 'Enables flexibility and high absorbency']} /> },
                ]}
              />
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-8 lg:p-10">
              <h3 className="text-lg font-medium text-white mb-5" >Veterinary Applications</h3>
              <HorizontalBorderStack items={clinicalApps} accent={ACCENT} testid="vet-applications" />
            </div>
          </div>
        </div>
      </section>

      {/* ─── Species Coverage ─── */}
      <section className="py-20 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="uppercase text-xs font-bold tracking-[0.2em] text-[#4CAF50] mb-3">Proven Results</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white" >Tested in Companion Animals</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { species: 'Dogs (Canine)', desc: 'Extensive clinical data from orthopedic and dental procedures. Most widely studied species for FlexiOss® Vet applications.' },
              { species: 'Cats (Feline)', desc: 'Safe and effective for feline maxillofacial and dental applications including stomatitis and periodontal disease.' },
              { species: 'Horses (Equine)', desc: 'Proven in EOTRH syndrome treatment, dental extractions, and periapical infections in equine patients.' },
            ].map((a, i) => (
              <div key={i} className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700 rounded-2xl p-7 hover:border-[#4CAF50] hover:shadow-md transition-all">
                <h3 className="text-lg font-semibold text-white mb-2" >{a.species}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Product Specifications Table ─── */}
      <section ref={specRef} className="py-20 bg-slate-900" data-testid="vet-specs">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="uppercase text-xs font-bold tracking-[0.2em] text-[#4CAF50] mb-3">Offer & Specification</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white" >Product Sizes & Specifications</h2>
          </div>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-[#4CAF50] to-[#388E3C] text-white">
                  <th className="px-4 py-3 text-left font-semibold rounded-tl-xl">Size</th>
                  <th className="px-4 py-3 text-left font-semibold">Pcs/Pack</th>
                  <th className="px-4 py-3 text-left font-semibold">EAN</th>
                  <th className="px-4 py-3 text-left font-semibold">Dimensions (wet)</th>
                  <th className="px-4 py-3 text-left font-semibold">Weight (dry)</th>
                  <th className="px-4 py-3 text-left font-semibold rounded-tr-xl">Geometry</th>
                </tr>
              </thead>
              <tbody>
                {productSizes.map((s, i) => (
                  <motion.tr key={i} initial={{ opacity: 0, x: -12 }} animate={specInView ? { opacity: 1, x: 0 } : {}} transition={{ delay: i * 0.06, duration: 0.4 }} className={`border-b border-slate-700 ${i % 2 === 0 ? 'bg-gradient-to-br from-slate-800/80 to-slate-900/80' : 'bg-slate-800/60'}`} data-testid={`spec-row-${i}`}>
                    <td className="px-4 py-3 font-bold text-white">FlexiOss® Vet {s.size}</td>
                    <td className="px-4 py-3 text-slate-400">{s.pieces}</td>
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{s.ean}</td>
                    <td className="px-4 py-3 text-slate-400">{s.dimL}<br />{s.dimD}</td>
                    <td className="px-4 py-3 text-slate-400">{s.weight}</td>
                    <td className="px-4 py-3 text-slate-400">{s.geometry}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden space-y-4">
            {productSizes.map((s, i) => (
              <div key={i} className="bg-slate-800/60 border border-slate-700 rounded-xl p-5">
                <h4 className="font-bold text-white mb-3">FlexiOss® Vet {s.size}</h4>
                <div className="grid grid-cols-2 gap-y-2 text-sm">
                  <span className="text-slate-400">Pcs/Pack:</span><span className="text-white font-semibold">{s.pieces}</span>
                  <span className="text-slate-400">Dimensions:</span><span className="text-white font-semibold">{s.dimL}, {s.dimD}</span>
                  <span className="text-slate-400">Weight:</span><span className="text-white font-semibold">{s.weight}</span>
                  <span className="text-slate-400">Geometry:</span><span className="text-white font-semibold">{s.geometry}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Available Sets ─── */}
      <section className="py-20 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900" data-testid="vet-sets">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="uppercase text-xs font-bold tracking-[0.2em] text-[#4CAF50] mb-3">Special Offer</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white" >Promotional Sets</h2>
            <p className="text-sm text-slate-400 mt-3 max-w-2xl mx-auto">Financially efficient assortment packages tailored to your clinic's profile and patient needs.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {availableSets.map((set, i) => (
              <div key={i} className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700 rounded-2xl p-7 hover:border-[#4CAF50] hover:shadow-md transition-all" data-testid={`vet-set-${i}`}>
                <h3 className="text-lg font-semibold text-white mb-1" >FlexiOss® Vet</h3>
                <p className="text-xl font-bold text-[#4CAF50] mb-4">{set.name}</p>
                <div className="mb-4">
                  <HorizontalBorderStack items={set.contents} accent={ACCENT} numbered={false} dense testid={`vet-set-contents-${i}`} />
                </div>
                <p className="text-xs text-slate-400 border-t border-slate-700 pt-3"><span className="font-semibold text-white">Use:</span> {set.use}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Case Studies ─── */}
      <section className="py-20 bg-slate-900" data-testid="vet-cases">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="uppercase text-xs font-bold tracking-[0.2em] text-[#4CAF50] mb-3">Application</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white" >Clinical Case Studies</h2>
          </div>
          {/* Filter */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-slate-800/60 rounded-xl p-1.5 border border-slate-700">
              {['All', 'Orthopaedics', 'Dentistry'].map((f) => (
                <button key={f} onClick={() => setCaseFilter(f)} data-testid={`case-filter-${f.toLowerCase()}`} className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${caseFilter === f ? 'bg-gradient-to-br from-slate-800/80 to-slate-900/80 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}>
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredCases.map((cs, i) => (
              <a key={i} href={cs.url} target="_blank" rel="noopener noreferrer" data-testid={`case-study-${i}`} className="group bg-slate-800/60 border border-slate-700 rounded-xl p-5 hover:border-[#4CAF50] hover:shadow-md transition-all">
                <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-[#4CAF50] bg-[#4CAF50]/10 px-2 py-0.5 rounded-full mb-3">{cs.category}</span>
                <h4 className="text-sm font-semibold text-white leading-snug mb-3 group-hover:text-[#4CAF50] transition-colors line-clamp-3">{cs.title}</h4>
                <span className="text-xs text-[#4CAF50] font-semibold inline-flex items-center gap-1">Read more <ChevronRight className="w-3 h-3" /></span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Publications ─── */}
      <ProductPublications
        publications={publications}
        dl={downloadFile}
        accent={ACCENT}
        testidPrefix="vet"
        bg="bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900"
        heading={<>Veterinary <span className="text-[#4CAF50]">Publications</span></>}
      />

      {/* ─── CTA ─── */}
      <section className="py-20 bg-slate-900" data-testid="vet-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <p className="uppercase text-xs font-bold tracking-[0.2em] text-[#4CAF50] mb-3">Get Started</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white mb-4" >
            Ready to Experience FlexiOss® <span className="text-[#4CAF50]">Vet</span>?
          </h2>
          <p className="text-base text-slate-400 mb-8 max-w-xl mx-auto">
            Contact our veterinary sales team for product information, pricing, and ordering. Available in promotional sets tailored to your practice.
          </p>
          <Link to="/" data-testid="vet-contact-sales" className="bg-[#4CAF50] hover:bg-[#388E3C] text-white font-bold px-8 py-4 rounded-lg text-sm shadow-2xl shadow-[#4CAF50]/50 hover:-translate-y-0.5 transition-all inline-flex items-center gap-2">
            Request for Samples <ChevronRight className="w-4 h-4" />
          </Link>
          <div className="mt-10 flex justify-center">
            <ProductShareBar title="FlexiOss® Vet" subject="FlexiOss® Vet — bone regeneration for veterinary surgery" summary={VET_SHARE_SUMMARY} pageUrl={`${SITE_URL}/flexioss-vet`} accent={ACCENT} testidPrefix="vet" />
          </div>
          <div className="mt-10 text-left"><SocialShare pageKey="vet" accent={ACCENT} /></div>
        </div>
      </section>
    </SubpageLayout>
  );
};

export default FlexiOssVetPage;
